import React, { useEffect, useRef, useState } from 'react';
import { Upload, Modal, Button, Progress, message } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import showAlertNotification from '../Modal/AlertNotification';
import { useUploadToS3Mutation, useLazyGetPresignedUrlQuery } from 'services';
import { AxiosProgressEvent } from 'axios';
import { ExcelFileIcon, imageEmpty } from 'utils/importFile';
import { CloudArrowUp, Upload as UploadIcon } from '@phosphor-icons/react';
import { ButtonCommon } from '../Button/ButtonCommon';
import { RcFile } from 'antd/es/upload';
import { UploadFileCommonProps } from 'types/common';

const getBase64 = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const UploadFileCommon: React.FC<UploadFileCommonProps> = ({
  filesData,
  setFilesData,
  isDisabled = false,
  accept = 'image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  maxSizeMB = 5,
  maxCount = 5,
  onNewFileUpload = () => {},
  onRemoveFile = () => {},
  readOnly = false,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadToS3] = useUploadToS3Mutation();
  const [getPresignedUrl] = useLazyGetPresignedUrlQuery();
  const uploadingCountRef = useRef(0);
  const shownErrorRef = useRef(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUploadingRef = useRef(false); // Track if currently uploading

  const resetErrorShown = () => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    errorTimeoutRef.current = setTimeout(() => {
      shownErrorRef.current = false;
    }, 3000);
  };

  useEffect(() => {
    if (filesData && filesData.length > 0) {
      setFileList(
        filesData.map((file, idx) => ({
          uid: file.id || String(idx),
          name: file.name,
          status: 'done',
          url: file.filePath,
          type: file.filePath?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)
            ? `image/${file.filePath.split('.').pop()}`
            : undefined,
        })) as UploadFile[],
      );
    } else {
      setFileList([]);
    }
  }, [filesData]);

  const handlePreview = async (file: UploadFile, filePath?: string) => {
    if (file.type?.startsWith('image/')) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as File);
      }
      setPreviewImage(file.url || (file.preview as string) || filePath || '');
      setPreviewOpen(true);
    } else if (filePath) {
      window.open(filePath, '_blank');
    }
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(0, maxCount));
  };

  const handleCancel = () => setPreviewOpen(false);

  const handleRemove = (file: UploadFile) => {
    const removedIndex = fileList.findIndex((item) => item.uid === file.uid);
    setFilesData((prev) => {
      const updated = prev
        .filter((_, index) => index !== removedIndex)
        .map((item, idx) => ({
          ...item,
          order: idx + 1,
        }));
      return updated;
    });
    if (onRemoveFile) {
      onRemoveFile(file.url || '');
    }
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
    message.success('Đã xóa file');
  };

  const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
    // Check file type
    const isAccepted = accept
      .split(',')
      .some(
        (type) =>
          type.trim() === file.type ||
          (type.trim().endsWith('/*') && file.type.startsWith(type.trim().replace('/*', ''))),
      );

    if (!isAccepted) {
      return {
        isValid: false,
        errorMessage: `Định dạng của file "${file.name}" không hợp lệ. Chỉ chấp nhận các định dạng: ${accept
          .split(',')
          .map((type) => type.trim().replace('image/', '').replace('application/', '').replace('/*', ''))
          .join(', ')}.`,
      };
    }

    // Check file size
    const isLtSize = file.size / 1024 / 1024 <= maxSizeMB;
    if (!isLtSize) {
      return {
        isValid: false,
        errorMessage: `Kích thước của file "${file.name}" phải nhỏ hơn hoặc bằng ${maxSizeMB}MB. Kích thước hiện tại: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB.`,
      };
    }

    return { isValid: true };
  };

  const beforeUpload: UploadProps['beforeUpload'] = async (file, fileListArray) => {
    // Prevent multiple file selection or drag-drop
    if (fileListArray && fileListArray.length > 1) {
      if (!shownErrorRef.current) {
        showAlertNotification({
          type: 'error',
          title: 'Tải file thất bại',
          message: 'Vui lòng chỉ chọn một file tại một thời điểm.',
        });
        shownErrorRef.current = true;
        resetErrorShown();
      }
      return Upload.LIST_IGNORE;
    }

    // Check if currently uploading
    if (isUploadingRef.current) {
      if (!shownErrorRef.current) {
        showAlertNotification({
          type: 'error',
          title: 'Tải file thất bại',
          message: 'Vui lòng đợi file hiện tại upload xong trước khi upload file mới.',
        });
        shownErrorRef.current = true;
        resetErrorShown();
      }
      return Upload.LIST_IGNORE;
    }

    // Check file count limit
    const totalCount = fileList.length + 1;
    if (totalCount > maxCount) {
      if (!shownErrorRef.current) {
        showAlertNotification({
          type: 'error',
          title: 'Tải file thất bại',
          message: `Bạn chỉ được tải tối đa ${maxCount} file.`,
        });
        shownErrorRef.current = true;
        resetErrorShown();
      }
      return Upload.LIST_IGNORE;
    }

    // Process single file
    processFileUpload(file);
    return Upload.LIST_IGNORE;
  };

  const processFileUpload = async (file: RcFile): Promise<void> => {
    // Set uploading flag
    isUploadingRef.current = true;

    // Validate the file first
    const validation = validateFile(file);
    if (!validation.isValid) {
      if (!shownErrorRef.current) {
        showAlertNotification({
          type: 'error',
          title: 'Tải file thất bại',
          message: validation.errorMessage || 'File không hợp lệ',
        });
        shownErrorRef.current = true;
        resetErrorShown();
      }
      isUploadingRef.current = false;
      return;
    }

    uploadingCountRef.current += 1;

    try {
      const fileName = `${Date.now()}-${file.name}`;
      const fileType = file.type;

      // Create a file item with uploading status
      const newFileItem: UploadFile = {
        uid: `upload-${Date.now()}-${Math.random()}-${file.name}`,
        name: file.name,
        status: 'uploading',
        percent: 0,
        originFileObj: file,
        type: file.type,
      };

      // Add to file list
      setFileList((prev) => [...prev, newFileItem]);

      // Get presigned URL and upload to S3
      const { preSignedURL, fileUrl } = await getPresignedUrl({ fileName, fileType }).unwrap();

      await uploadToS3({
        preSignedURL,
        file,
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (event.total) {
            const percent = Math.round((event.loaded * 100) / event.total);
            setFileList((prevList) =>
              prevList.map((item) =>
                item.uid === newFileItem.uid
                  ? { ...item, percent, status: percent === 100 ? 'done' : 'uploading' }
                  : item,
              ),
            );
          }
        },
      }).unwrap();

      // Upload successful, update filesData
      const uploadedFileUrl = fileUrl.split('?')[0];
      setFilesData((prev) => [
        ...prev,
        {
          id: newFileItem.uid,
          filePath: uploadedFileUrl,
          name: fileName,
          order: prev.length + 1,
        },
      ]);

      if (onNewFileUpload) {
        onNewFileUpload(uploadedFileUrl, fileName);
      }
    } catch (error) {
      console.error('Upload failed:', error);

      // Update file status to error
      setFileList((prevList) =>
        prevList.map((item) =>
          item.name === file.name && item.status === 'uploading' ? { ...item, status: 'error' } : item,
        ),
      );

      if (!shownErrorRef.current) {
        showAlertNotification({
          type: 'error',
          title: 'Tải file thất bại',
          message: `Không thể tải lên file "${file.name}". Vui lòng thử lại.`,
        });
        shownErrorRef.current = true;
        resetErrorShown();
      }
    } finally {
      uploadingCountRef.current -= 1;
      isUploadingRef.current = false; // Reset uploading flag
    }
  };

  const uploadButton = (
    <div className='flex justify-center items-center gap-6'>
      <CloudArrowUp size={48} weight='thin' />
      <div className='flex justify-between items-center w-full'>
        <div className='text-left'>
          <p>Chọn hoặc kéo thả chứng từ vào đây...</p>
          <p className='text-[var(--textBlack56)]'>{`Chứng từ phải ở định dạng excel, pdf, docx, image/*... và không vượt quá ${maxSizeMB}MB`}</p>
        </div>
        <ButtonCommon
          color='basic'
          fullWidth
          radius='rounded'
          size='sm'
          className='bg-[var(--bgBlackOpacity)] max-w-[132px] text-black gap-2'
          icon={<UploadIcon size={20} />}
          iconPosition='left'
        >
          <span className='whitespace-nowrap'>Chọn Tệp Tin</span>
        </ButtonCommon>
      </div>
    </div>
  );

  return (
    <>
      {!isDisabled && (
        <Upload.Dragger
          listType='picture-card'
          fileList={fileList}
          accept={accept}
          className={`p-0 upload-image-drag custom-upload ${
            fileList.length >= maxCount || isUploadingRef.current ? 'opacity-50 pointer-events-none' : ''
          }`}
          onChange={handleChange}
          beforeUpload={beforeUpload}
          multiple={false} // Disable multiple file selection
          disabled={fileList.length >= maxCount || isUploadingRef.current}
        >
          {uploadButton}
        </Upload.Dragger>
      )}

      <Modal open={previewOpen} footer={null} onCancel={handleCancel} centered>
        <img alt='preview' style={{ width: '100%' }} src={previewImage} />
      </Modal>

      <div className='flex gap-4 mt-4 flex-wrap'>
        {Array.from({ length: maxCount }).map((_, index) => {
          const file = fileList[index];
          const fileData = filesData[index];
          const isImage =
            file?.type?.startsWith('image/') ||
            (fileData?.filePath && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileData.filePath));
          return (
            <div
              key={index}
              className='w-[84px] h-[84px] relative flex items-center justify-center bg-gray-50 rounded-[16px] border border-[var(--textBlack16)]'
            >
              {fileData?.filePath ? (
                isImage ? (
                  <img src={fileData.filePath} alt='thumbnail' className='w-full h-full object-cover rounded-lg' />
                ) : (
                  <img src={ExcelFileIcon} alt='file' className='w-10 h-10 mx-auto' />
                )
              ) : (
                <>
                  {file && file.status === 'done' ? (
                    isImage ? (
                      <img
                        src={
                          file.thumbUrl ||
                          (file.preview as string) ||
                          (file.originFileObj ? URL.createObjectURL(file.originFileObj as Blob) : '')
                        }
                        alt='thumbnail'
                        className='w-full h-full object-cover rounded-lg'
                      />
                    ) : (
                      <img src={ExcelFileIcon} alt='file' className='w-10 h-10 mx-auto' />
                    )
                  ) : file?.status === 'uploading' ? (
                    <div className='flex flex-col items-center justify-center w-full'>
                      <Progress percent={file?.percent} size='small' status='active' className='w-[80%]' />
                      <span className='text-xs mt-1'>Đang tải...</span>
                    </div>
                  ) : file?.status === 'error' ? (
                    <div className='flex flex-col items-center justify-center text-red-500'>
                      <span className='text-xs text-center'>Lỗi tải file</span>
                    </div>
                  ) : (
                    <div className='w-full h-full flex items-center justify-center'>
                      <img src={imageEmpty} alt='No products available' />
                    </div>
                  )}
                </>
              )}

              {file && file.status === 'done' && (
                <>
                  <Button
                    icon={<EyeOutlined />}
                    size='small'
                    className='absolute top-2 left-2 bg-black bg-opacity-50 border-none text-white rounded-full'
                    onClick={() =>
                      isImage ? handlePreview(file, fileData?.filePath) : window.open(fileData?.filePath, '_blank')
                    }
                  />
                  {!readOnly && (
                    <Button
                      icon={<DeleteOutlined />}
                      size='small'
                      className='absolute top-2 right-2 bg-red-600 bg-opacity-70 border-none text-white rounded-full'
                      onClick={() => handleRemove(file)}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default UploadFileCommon;
